import React, { useMemo } from 'react';
import './OnboardingProgressBar.css';

export interface OnboardingProgressStepMeta {
  label: string;
  subtitle: string;
}

export interface OnboardingProgressBarProps {
  /** Zero-based current step index */
  currentStep: number;
  steps: OnboardingProgressStepMeta[];
  /** Called when user clicks a completed step (optional) */
  onStepClick?: (stepIndex: number) => void;
  /** Use compact layout (e.g. narrow screens) */
  compact?: boolean;
}

export const OnboardingProgressBar: React.FC<OnboardingProgressBarProps> = ({
  currentStep,
  steps,
  onStepClick,
  compact = false,
}) => {
  const total = steps.length;
  const displayStep = Math.min(currentStep + 1, total);
  const currentMeta = steps[currentStep] ?? steps[0];
  const ariaLabel = useMemo(
    () =>
      `Onboarding progress: step ${displayStep} of ${total}, ${currentMeta?.label ?? ''}. ${currentMeta?.subtitle ?? ''}`,
    [displayStep, total, currentMeta?.label, currentMeta?.subtitle]
  );

  return (
    <div className={`onboarding-progress${compact ? ' onboarding-progress--compact' : ''}`}>
      <div
        className="onboarding-progress__top"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={displayStep}
        aria-valuetext={`Step ${displayStep} of ${total}: ${currentMeta?.subtitle ?? ''}`}
        aria-label={ariaLabel}
      >
        <span className="onboarding-progress__label">
          Step {displayStep} of {total}
        </span>
        {!compact ? (
          <p className="onboarding-progress__subtitle" id="onboarding-progress-subtitle">
            {currentMeta?.subtitle}
          </p>
        ) : null}
      </div>

      <div className="onboarding-progress__segments" aria-hidden={compact ? undefined : true}>
        {steps.map((s, i) => {
          const isComplete = i < currentStep;
          const isCurrent = i === currentStep;
          const isFuture = i > currentStep;
          const clickable = Boolean(onStepClick) && isComplete;
          const segClass = [
            'onboarding-progress__segment',
            isComplete ? 'onboarding-progress__segment--complete' : '',
            isCurrent ? 'onboarding-progress__segment--current' : '',
            isFuture ? 'onboarding-progress__segment--future' : '',
            clickable ? 'onboarding-progress__segment--clickable' : '',
          ]
            .filter(Boolean)
            .join(' ');

          if (clickable && onStepClick) {
            return (
              <div key={s.label} className="onboarding-progress__segment-wrap">
                <button
                  type="button"
                  className={segClass}
                  title={`Go back to ${s.label}`}
                  aria-label={`Go back to step ${i + 1}: ${s.label}`}
                  onClick={() => onStepClick(i)}
                >
                  <span className="sr-only">
                    Step {i + 1}: {s.label}
                  </span>
                </button>
              </div>
            );
          }

          return (
            <div key={s.label} className="onboarding-progress__segment-wrap">
              <span
                className={segClass}
                title={isFuture ? 'Complete previous steps first' : s.label}
                aria-hidden="true"
              />
            </div>
          );
        })}
      </div>

      {!compact ? (
        <div className="onboarding-progress__segment-labels">
          {steps.map((s, i) => (
            <span
              key={s.label}
              className={`onboarding-progress__segment-caption${
                i === currentStep ? ' onboarding-progress__segment-caption--active' : ''
              }`}
            >
              {s.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};
